import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ApiClientError } from '../../services/apiClient'
import {
  formatMxn,
  suscripcionClient,
  type CallbackPaymentInfo,
} from '../../services/suscripcionClient'

/**
 * Página pública del callback del banco §10.4 paso 5.
 *
 * URL: /confirmacion/{ref256}?id=XXX
 *
 * EvoClick redirige al usuario aquí tras pago exitoso, con ?id=XXX (confirmación del banco).
 * Si viene ?id, llamamos POST /api/confirmacion/{ref256} para activar la suscripción.
 * Luego hacemos polling GET cada 3s durante 30s para mostrar el resultado al usuario.
 */
export function ConfirmacionPage() {
  const { ref256 = '' } = useParams<{ ref256: string }>()
  const [searchParams] = useSearchParams()
  const bankId = searchParams.get('id') ?? ''

  const [info, setInfo] = useState<CallbackPaymentInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [pollingExhausted, setPollingExhausted] = useState(false)

  useEffect(() => {
    if (!ref256) {
      setError('Referencia no proporcionada.')
      setLoading(false)
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let attemptsLeft = 10  // 10 × 3s = 30s de polling.

    function pollStatus() {
      suscripcionClient
        .showCallback(ref256)
        .then((r) => {
          if (cancelled) return
          setInfo(r)
          setError(null)
          setLoading(false)
          if (r.payment.status === 'pending') {
            if (attemptsLeft > 0) {
              attemptsLeft -= 1
              timer = setTimeout(() => { if (!cancelled) pollStatus() }, 3000)
            } else {
              setPollingExhausted(true)
            }
          }
        })
        .catch((err) => {
          if (cancelled) return
          setLoading(false)
          setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo consultar.')
        })
    }

    // Si EvoClick redirigió con ?id=XXX, confirmamos el pago primero y luego hacemos polling.
    if (bankId) {
      suscripcionClient
        .confirmCallback(ref256, bankId)
        .catch(() => {
          // Si ya fue confirmado (409) o hay error, igual hacemos polling para mostrar estado real.
        })
        .finally(() => {
          if (!cancelled) pollStatus()
        })
    } else {
      pollStatus()
    }


    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [ref256])

  return (
    <main className="page page--centered">
      <div className="card" style={{ maxWidth: 560, width: '100%' }}>
        <h1 style={{ marginTop: 0 }}>Confirmación de pago</h1>

        {loading && <p className="muted">Consultando estado del pago…</p>}
        {error && <p className="error">{error}</p>}

        {info && (
          <div className="content-stack" style={{ gap: '.6rem' }}>
            <StatusBlock status={info.payment.status} pollingExhausted={pollingExhausted} />

            <dl className="kv">
              <dt>Referencia</dt>
              <dd><span className="mono">{info.payment.reference}</span></dd>
              <dt>Monto</dt>
              <dd>{formatMxn(info.payment.amount)}</dd>
              {info.plan && (
                <>
                  <dt>Plan</dt>
                  <dd>{info.plan.name}</dd>
                </>
              )}
              {info.payment.paid_at && (
                <>
                  <dt>Pagado el</dt>
                  <dd>{new Date(info.payment.paid_at.replace(' ', 'T')).toLocaleString('es-MX')}</dd>
                </>
              )}
              {info.payment.bank_confirmation && (
                <>
                  <dt>Confirmación bancaria</dt>
                  <dd><span className="mono">{info.payment.bank_confirmation}</span></dd>
                </>
              )}
            </dl>

            <div className="modal__actions">
              <Link to="/suscripcion" className="primary-btn">Volver a Suscripción</Link>
            </div>
          </div>
        )}
      </div>
    </main>
  )
}

function StatusBlock({ status, pollingExhausted }: { status: 'pending' | 'paid' | 'failed'; pollingExhausted: boolean }) {
  if (status === 'paid') {
    return (
      <p className="alert alert--success">
        ✓ Pago confirmado. Tu suscripción ya está activa.
      </p>
    )
  }
  if (status === 'failed') {
    return (
      <p className="alert alert--error">
        ✗ El pago no pudo procesarse. Inicia un nuevo intento desde la página de Suscripción.
      </p>
    )
  }
  return (
    <p className="alert alert--info">
      Pago en proceso de confirmación.{' '}
      {pollingExhausted
        ? 'Si tu pago ya fue exitoso, la activación llegará en breve. Puedes recargar esta página en unos minutos.'
        : 'Verificando automáticamente…'}
    </p>
  )
}
